/**
 * Jubly Work — Google Drive-like file management
 * Projects, folders, files with tags and preview
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FileRenderer from '../components/files/FileRenderer';
import {
  FolderPlus, FilePlus, Upload, Search, Grid3X3, List, Star, Trash2,
  ChevronRight, ChevronDown, MoreVertical, Tag, X, Plus, ArrowLeft,
  File, FileText, Image, Film, Music, Archive, Code, Table, Presentation,
  Eye, Download, Edit2, FolderOpen, Hash, Users, Link2, Globe, Lock, ExternalLink
} from 'lucide-react';

const API = '/api/work';

const IMAGIA_API = import.meta.env.VITE_IMAGIA_API_URL || 'https://imagia.net';
const PARTNER_KEY = import.meta.env.VITE_IMAGIA_PARTNER_KEY || '';

async function partnerApi(method, path) {
  const res = await fetch(`${IMAGIA_API}/api/partner${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-Partner-Key': PARTNER_KEY },
  });
  if (!res.ok) return null;
  return res.json();
}

// File type icon with extension-aware colors
function FileIcon({ mimeType, name, className = 'w-5 h-5' }) {
  const ext = name?.split('.').pop()?.toLowerCase() || '';
  if (!mimeType && !ext) return <File className={className} />;

  // Extension-specific icons
  const extMap = {
    // Documents
    doc: { icon: FileText, color: 'text-blue-700', bg: 'bg-blue-100' },
    docx: { icon: FileText, color: 'text-blue-700', bg: 'bg-blue-100' },
    pdf: { icon: FileText, color: 'text-red-600', bg: 'bg-red-100' },
    txt: { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
    rtf: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
    md: { icon: Code, color: 'text-gray-700', bg: 'bg-gray-100' },
    // Spreadsheets
    xls: { icon: Table, color: 'text-green-700', bg: 'bg-green-100' },
    xlsx: { icon: Table, color: 'text-green-700', bg: 'bg-green-100' },
    csv: { icon: Table, color: 'text-green-600', bg: 'bg-green-50' },
    // Presentations
    ppt: { icon: Presentation, color: 'text-orange-600', bg: 'bg-orange-100' },
    pptx: { icon: Presentation, color: 'text-orange-600', bg: 'bg-orange-100' },
    // Code
    js: { icon: Code, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    ts: { icon: Code, color: 'text-blue-600', bg: 'bg-blue-50' },
    py: { icon: Code, color: 'text-green-600', bg: 'bg-green-50' },
    html: { icon: Code, color: 'text-orange-500', bg: 'bg-orange-50' },
    css: { icon: Code, color: 'text-purple-500', bg: 'bg-purple-50' },
    json: { icon: Code, color: 'text-gray-600', bg: 'bg-gray-100' },
    // Archives
    zip: { icon: Archive, color: 'text-amber-700', bg: 'bg-amber-100' },
    rar: { icon: Archive, color: 'text-amber-700', bg: 'bg-amber-100' },
    gz: { icon: Archive, color: 'text-amber-700', bg: 'bg-amber-100' },
    // Images
    png: { icon: Image, color: 'text-green-500', bg: 'bg-green-50' },
    jpg: { icon: Image, color: 'text-green-500', bg: 'bg-green-50' },
    jpeg: { icon: Image, color: 'text-green-500', bg: 'bg-green-50' },
    gif: { icon: Image, color: 'text-purple-500', bg: 'bg-purple-50' },
    svg: { icon: Image, color: 'text-orange-500', bg: 'bg-orange-50' },
    webp: { icon: Image, color: 'text-green-500', bg: 'bg-green-50' },
    // Video
    mp4: { icon: Film, color: 'text-purple-600', bg: 'bg-purple-100' },
    mov: { icon: Film, color: 'text-purple-600', bg: 'bg-purple-100' },
    avi: { icon: Film, color: 'text-purple-600', bg: 'bg-purple-100' },
    webm: { icon: Film, color: 'text-purple-600', bg: 'bg-purple-100' },
    // Audio
    mp3: { icon: Music, color: 'text-pink-500', bg: 'bg-pink-100' },
    wav: { icon: Music, color: 'text-pink-500', bg: 'bg-pink-100' },
    m4a: { icon: Music, color: 'text-pink-500', bg: 'bg-pink-100' },
  };

  const match = extMap[ext];
  if (match) {
    const Icon = match.icon;
    return <Icon className={`${className} ${match.color}`} />;
  }

  // MIME fallback
  if (mimeType?.startsWith('image/')) return <Image className={`${className} text-green-500`} />;
  if (mimeType?.startsWith('video/')) return <Film className={`${className} text-purple-500`} />;
  if (mimeType?.startsWith('audio/')) return <Music className={`${className} text-pink-500`} />;
  return <File className={`${className} text-gray-400`} />;
}

// Extension badge (shows colored file extension)
function ExtBadge({ name }) {
  const ext = name?.split('.').pop()?.toLowerCase() || '';
  if (!ext || ext === name?.toLowerCase()) return null;
  const colors = {
    docx: 'bg-blue-600', doc: 'bg-blue-600', pdf: 'bg-red-600', txt: 'bg-gray-500',
    xlsx: 'bg-green-700', xls: 'bg-green-700', csv: 'bg-green-600',
    pptx: 'bg-orange-600', ppt: 'bg-orange-600',
    png: 'bg-green-500', jpg: 'bg-green-500', jpeg: 'bg-green-500', gif: 'bg-purple-500', svg: 'bg-orange-500',
    mp4: 'bg-purple-600', mov: 'bg-purple-600', mp3: 'bg-pink-500', wav: 'bg-pink-500',
    zip: 'bg-amber-700', rar: 'bg-amber-700',
    js: 'bg-yellow-600', ts: 'bg-blue-600', py: 'bg-green-600', html: 'bg-orange-500', css: 'bg-purple-500', json: 'bg-gray-600', md: 'bg-gray-700',
  };
  return (
    <span className={`text-[9px] font-bold text-white px-1 py-px rounded uppercase ${colors[ext] || 'bg-gray-400'}`}>
      {ext}
    </span>
  );
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Preview Modal
function FilePreview({ file, onClose, onFileChanged }) {
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [rawUrl, setRawUrl] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [shareEmail, setShareEmail] = useState('');

  const handleDownloadFile = async () => {
    try {
      const res = await fetch(`${API}/files/${file.id}/raw`, { credentials: 'include' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback to download endpoint
      window.open(`${API}/files/${file.id}/download`, '_blank');
    }
  };

  useEffect(() => {
    if (file?.id) {
      setDownloadUrl(null);
      setRawUrl(null);
      setRenaming(false);
      setNewName(file.name || '');
      fetch(`${API}/files/${file.id}`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          console.log('[FilePreview] API response:', { downloadUrl: d.file?.downloadUrl, rawUrl: d.file?.rawUrl, name: d.file?.name });
          setDownloadUrl(d.file?.downloadUrl || null);
          setRawUrl(d.file?.rawUrl || null);
        })
        .catch(() => {});
    }
  }, [file?.id]);

  // Office document renderer (must be before early return)
  const docxRef = useRef(null);
  const [xlsxHtml, setXlsxHtml] = useState('');
  const [xlsxSheets, setXlsxSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [pptxSlides, setPptxSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const slideContainerRef = useRef(null);
  const [slideScale, setSlideScale] = useState(1);

  // Track slide container width for proportional font sizing
  // Standard PPTX slide = 720pt wide; scale = containerPx / 720
  useEffect(() => {
    const el = slideContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setSlideScale(entry.contentRect.width / 720);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  });

  useEffect(() => {
    if (!file) { setXlsxHtml(''); setXlsxSheets([]); setPptxSlides([]); return; }
    const ext = file.name?.split('.').pop()?.toLowerCase();
    // Office files use rawUrl (our proxy), everything else uses downloadUrl
    const fetchUrl = rawUrl || downloadUrl;
    if (!fetchUrl) { setXlsxHtml(''); setXlsxSheets([]); setPptxSlides([]); return; }

    // DOCX — fetch via rawUrl (authenticated proxy, no browser download)
    if (ext === 'docx' && docxRef.current && rawUrl) {
      import('docx-preview').then(({ renderAsync }) => {
        fetch(rawUrl, { credentials: 'include' })
          .then(r => r.arrayBuffer())
          .then(data => {
            if (docxRef.current) {
              docxRef.current.innerHTML = '';
              renderAsync(data, docxRef.current, null, { className: 'docx-preview', inWrapper: true });
            }
          })
          .catch(err => console.warn('DOCX render failed:', err));
      });
    }

    // XLSX — fetch via rawUrl
    if ((ext === 'xlsx' || ext === 'xls' || ext === 'csv') && rawUrl) {
      setXlsxHtml('<div class="p-4 text-gray-400 text-sm">Loading spreadsheet...</div>');
      fetch(rawUrl, { credentials: 'include' })
        .then(r => r.arrayBuffer())
        .then(data => {
          import('xlsx').then(XLSX => {
            const wb = XLSX.read(data, { type: 'array' });
            setXlsxSheets(wb.SheetNames);
            setActiveSheet(0);
            const ws = wb.Sheets[wb.SheetNames[0]];
            setXlsxHtml(XLSX.utils.sheet_to_html(ws, { editable: false }));
          });
        })
        .catch(err => { console.warn('XLSX parse failed:', err); setXlsxHtml('<div class="p-4 text-red-400 text-sm">Failed to load spreadsheet</div>'); });
    }

    // PPTX — rich parser: extract shapes, text formatting, colors, positions
    if (ext === 'pptx' && rawUrl) {
      setPptxSlides([{ shapes: [], number: 1, bg: '#fff' }]);
      fetch(rawUrl, { credentials: 'include' })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
        .then(async (data) => {
          try {
            const JSZip = (await import('jszip')).default;
            const zip = await JSZip.loadAsync(data);

            // Collect embedded images as blob URLs
            const imageMap = {};
            for (const [path, entry] of Object.entries(zip.files)) {
              if (path.startsWith('ppt/media/') && !entry.dir) {
                try {
                  const blob = await entry.async('blob');
                  const ext2 = path.split('.').pop().toLowerCase();
                  const mime2 = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', svg: 'image/svg+xml', emf: 'image/emf', wmf: 'image/wmf' }[ext2] || 'image/png';
                  imageMap[path] = URL.createObjectURL(new Blob([blob], { type: mime2 }));
                } catch {}
              }
            }

            // Parse slide relationships for image references
            const parseRels = async (slideNum) => {
              const relsPath = `ppt/slides/_rels/slide${slideNum}.xml.rels`;
              const rels = {};
              if (zip.files[relsPath]) {
                const xml = await zip.files[relsPath].async('text');
                const matches = xml.match(/<Relationship[^>]+>/g) || [];
                matches.forEach(m => {
                  const id = m.match(/Id="([^"]+)"/)?.[1];
                  const target = m.match(/Target="([^"]+)"/)?.[1];
                  if (id && target) {
                    const resolved = target.startsWith('../') ? 'ppt/' + target.replace('../', '') : target.startsWith('/') ? target.slice(1) : 'ppt/slides/' + target;
                    rels[id] = resolved;
                  }
                });
              }
              return rels;
            };

            // Detect slide dimensions from presentation.xml
            let SW = 9144000, SH = 6858000; // default 4:3
            try {
              const presXml = await zip.files['ppt/presentation.xml']?.async('text');
              if (presXml) {
                const sldSz = presXml.match(/<p:sldSz\s+cx="(\d+)"\s+cy="(\d+)"/);
                if (sldSz) { SW = parseInt(sldSz[1]); SH = parseInt(sldSz[2]); }
              }
            } catch {}
            // EMU to percentage of slide dimensions
            const emuPctX = v => (parseInt(v) / SW * 100);
            const emuPctY = v => (parseInt(v) / SH * 100);
            const emuPt = v => Math.round(parseInt(v) / 12700); // EMU to points

            // Parse actual theme colors from ppt/theme/theme1.xml
            const THEME_COLORS = {
              dk1: '#000000', dk2: '#44546A', lt1: '#FFFFFF', lt2: '#E7E6E6',
              accent1: '#4472C4', accent2: '#ED7D31', accent3: '#A5A5A5', accent4: '#FFC000',
              accent5: '#5B9BD5', accent6: '#70AD47', hlink: '#0563C1', folHlink: '#954F72',
              tx1: '#000000', tx2: '#44546A', bg1: '#FFFFFF', bg2: '#E7E6E6',
            };
            try {
              const themeXml = await zip.files['ppt/theme/theme1.xml']?.async('text');
              if (themeXml) {
                const schemeNames = ['dk1', 'dk2', 'lt1', 'lt2', 'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6', 'hlink', 'folHlink'];
                for (const name of schemeNames) {
                  const m = themeXml.match(new RegExp(`<a:${name}>[\\s\\S]*?<a:srgbClr val="([^"]+)"[\\s\\S]*?<\\/a:${name}>`));
                  if (m) THEME_COLORS[name] = '#' + m[1];
                  const sys = themeXml.match(new RegExp(`<a:${name}>[\\s\\S]*?<a:sysClr[^>]*lastClr="([^"]+)"[\\s\\S]*?<\\/a:${name}>`));
                  if (sys) THEME_COLORS[name] = '#' + sys[1];
                }
                THEME_COLORS.tx1 = THEME_COLORS.dk1;
                THEME_COLORS.tx2 = THEME_COLORS.dk2;
                THEME_COLORS.bg1 = THEME_COLORS.lt1;
                THEME_COLORS.bg2 = THEME_COLORS.lt2;
              }
            } catch {}

            // Extract color from XML fragment
            const getColor = (xml) => {
              const srgb = xml.match(/<a:srgbClr val="([^"]+)"/)?.[1];
              if (srgb) return '#' + srgb;
              const scheme = xml.match(/<a:schemeClr val="([^"]+)"/)?.[1];
              if (scheme && THEME_COLORS[scheme]) return THEME_COLORS[scheme];
              return null;
            };

            // Parse a text body into formatted runs
            const parseTextBody = (txXml) => {
              const paragraphs = [];
              const pMatches = txXml.match(/<a:p[\s>][\s\S]*?<\/a:p>/g) || [];
              for (const pXml of pMatches) {
                const align = pXml.match(/<a:pPr[^>]*algn="([^"]+)"/)?.[1] || 'l';
                const runs = [];
                // Match <a:r> with or without attributes, and also <a:r > variants
                const rMatches = pXml.match(/<a:r[\s>][\s\S]*?<\/a:r>/g) || [];
                for (const rXml of rMatches) {
                  // Match <a:t> with or without xml:space attribute
                  const textMatches = rXml.match(/<a:t[^>]*>[\s\S]*?<\/a:t>/g) || [];
                  const text = textMatches.map(t => t.replace(/<[^>]+>/g, '').trim()).join('');
                  if (!text) continue;
                  const rPrBlock = rXml.match(/<a:rPr[\s\S]*?(?:\/>|<\/a:rPr>)/)?.[0] || '';
                  const rPr = rXml.match(/<a:rPr[^>]*>/)?.[0] || '';
                  const bold = /\bb="1"/.test(rPr);
                  const italic = /\bi="1"/.test(rPr);
                  const sz = rPr.match(/\bsz="(\d+)"/)?.[1];
                  const color = getColor(rXml);
                  const fontFamily = rPrBlock.match(/<a:latin[^>]*typeface="([^"]+)"/)?.[1] || null;
                  runs.push({ text, bold, italic, size: sz ? parseInt(sz) / 100 : null, color, fontFamily });
                }
                // Also grab bare <a:t> text not inside <a:r> (direct text in paragraph)
                const bareTextMatches = pXml.replace(/<a:r[\s>][\s\S]*?<\/a:r>/g, '').match(/<a:t[^>]*>[\s\S]*?<\/a:t>/g) || [];
                for (const t of bareTextMatches) {
                  const text = t.replace(/<[^>]+>/g, '').trim();
                  if (text) runs.push({ text, bold: false, italic: false, size: null, color: null, fontFamily: null });
                }
                // Field text (slide numbers, dates)
                const fldMatches = pXml.match(/<a:fld[\s\S]*?<\/a:fld>/g) || [];
                for (const fXml of fldMatches) {
                  const text = (fXml.match(/<a:t[^>]*>[\s\S]*?<\/a:t>/) || [''])[0].replace(/<[^>]+>/g, '').trim();
                  if (text) runs.push({ text, bold: false, italic: false, size: null, color: null, fontFamily: null });
                }
                if (runs.length > 0) {
                  paragraphs.push({ align: { l: 'left', ctr: 'center', r: 'right', just: 'justify' }[align] || 'left', runs });
                }
              }
              return paragraphs;
            };

            const slides = [];
            let i = 1;
            while (zip.files[`ppt/slides/slide${i}.xml`]) {
              const xml = await zip.files[`ppt/slides/slide${i}.xml`].async('text');
              const rels = await parseRels(i);

              // Slide background color
              let bg = '#ffffff';
              const bgFill = xml.match(/<p:bg>[\s\S]*?<\/p:bg>/)?.[0];
              if (bgFill) {
                const c = getColor(bgFill);
                if (c) bg = c;
              }

              // Flatten group shapes with coordinate transforms
              let flatXml = xml;
              const grpMatches = xml.match(/<p:grpSp[\s>][\s\S]*?<\/p:grpSp>/g) || [];
              for (const grp of grpMatches) {
                const grpSpPr = grp.match(/<p:grpSpPr[\s>][\s\S]*?<\/p:grpSpPr>/)?.[0] || '';
                const grpOff = grpSpPr.match(/<a:off x="(\d+)" y="(\d+)"/);
                const grpExt = grpSpPr.match(/<a:ext cx="(\d+)" cy="(\d+)"/);
                const chOff = grpSpPr.match(/<a:chOff x="(\d+)" y="(\d+)"/);
                const chExt = grpSpPr.match(/<a:chExt cx="(\d+)" cy="(\d+)"/);

                const transformChild = (childXml) => {
                  if (grpOff && grpExt && chOff && chExt) {
                    const gox = parseInt(grpOff[1]), goy = parseInt(grpOff[2]);
                    const gex = parseInt(grpExt[1]), gey = parseInt(grpExt[2]);
                    const cox = parseInt(chOff[1]), coy = parseInt(chOff[2]);
                    const cex = parseInt(chExt[1]), cey = parseInt(chExt[2]);
                    const sx = cex > 0 ? gex / cex : 1, sy = cey > 0 ? gey / cey : 1;
                    return childXml
                      .replace(/<a:off x="(\d+)" y="(\d+)"/, (_, cx, cy) => `<a:off x="${Math.round(gox + (parseInt(cx) - cox) * sx)}" y="${Math.round(goy + (parseInt(cy) - coy) * sy)}"`)
                      .replace(/<a:ext cx="(\d+)" cy="(\d+)"/, (_, cw, ch) => `<a:ext cx="${Math.round(parseInt(cw) * sx)}" cy="${Math.round(parseInt(ch) * sy)}"`);
                  }
                  return childXml;
                };

                const childSps = (grp.match(/<p:sp[\s>][\s\S]*?<\/p:sp>/g) || []).map(transformChild);
                const childPics = (grp.match(/<p:pic[\s>][\s\S]*?<\/p:pic>/g) || []).map(transformChild);
                flatXml = flatXml.replace(grp, childSps.join('\n') + childPics.join('\n'));
              }

              // Parse shapes
              const shapes = [];
              const spMatches = flatXml.match(/<p:sp[\s>][\s\S]*?<\/p:sp>/g) || [];
              for (const spXml of spMatches) {
                const off = spXml.match(/<a:off x="(\d+)" y="(\d+)"/);
                const ext2 = spXml.match(/<a:ext cx="(\d+)" cy="(\d+)"/);
                if (!off || !ext2) continue;

                const prstGeom = spXml.match(/<a:prstGeom prst="([^"]+)"/)?.[1] || 'rect';
                const isEllipse = ['ellipse', 'donut', 'pie', 'arc', 'chord'].includes(prstGeom);

                const hasCustomGeom = /<a:custGeom/.test(spXml);
                const isFreeform = hasCustomGeom || ['freeform', 'line', 'straightConnector1'].includes(prstGeom);

                const shape = {
                  type: 'shape',
                  x: emuPctX(off[1]), y: emuPctY(off[2]),
                  w: emuPctX(ext2[1]), h: emuPctY(ext2[2]),
                  fill: null, borderColor: null, borderWidth: 0,
                  borderRadius: isEllipse ? '50%' : '4px',
                  opacity: 1,
                  paragraphs: [],
                };

                // Shape fill — scope to spPr to avoid picking up text fills
                const spPr = spXml.match(/<p:spPr[\s>][\s\S]*?<\/p:spPr>/)?.[0] || '';
                const fillMatch = spPr.match(/<a:solidFill>[\s\S]*?<\/a:solidFill>/);
                if (fillMatch) {
                  const c = getColor(fillMatch[0]);
                  if (c) shape.fill = c;
                  // Extract alpha
                  const alphaVal = fillMatch[0].match(/<a:alpha val="(\d+)"/)?.[1];
                  if (alphaVal) shape.opacity = parseInt(alphaVal) / 100000;
                }
                // Also check overall spPr for alpha
                if (shape.opacity === 1) {
                  const spAlpha = spPr.match(/<a:alpha val="(\d+)"/)?.[1];
                  if (spAlpha) shape.opacity = parseInt(spAlpha) / 100000;
                }
                // Check for noFill
                if (/<a:noFill\s*\/>/.test(spPr) && !shape.fill) shape.fill = 'transparent';
                // Check gradient fill
                if (!shape.fill) {
                  const gradFill = spPr.match(/<a:gradFill>[\s\S]*?<\/a:gradFill>/)?.[0];
                  if (gradFill) { const c = getColor(gradFill); if (c) shape.fill = c; }
                }
                // Freeform shapes — render as transparent with stroke
                if (isFreeform && shape.fill && shape.fill !== 'transparent') {
                  if (!shape.borderColor) shape.borderColor = shape.fill;
                  shape.fill = 'transparent';
                  shape.borderWidth = shape.borderWidth || 2;
                }

                // Shape border
                const lnMatch = spPr.match(/<a:ln[\s\S]*?<\/a:ln>/);
                if (lnMatch) {
                  const lnNoFill = /<a:noFill\s*\/>/.test(lnMatch[0]);
                  if (!lnNoFill) {
                    const c = getColor(lnMatch[0]);
                    if (c) shape.borderColor = c;
                  }
                  const w = lnMatch[0].match(/<a:ln w="(\d+)"/)?.[1];
                  if (w) shape.borderWidth = emuPt(w);
                }

                // Text body — try parseTextBody first, then direct extraction
                const txBody = spXml.match(/<p:txBody[\s>][\s\S]*?<\/p:txBody>/)?.[0];
                if (txBody) {
                  shape.paragraphs = parseTextBody(txBody);
                }
                // Always extract all text as fallback — match <a:t> with any content
                const allTexts = (spXml.match(/<a:t[^>]*>[\s\S]*?<\/a:t>/g) || [])
                  .map(t => t.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
                shape.allText = allTexts.join(' ');
                if (!shape.paragraphs?.length && allTexts.length > 0) {
                  shape.paragraphs = [{ align: 'center', runs: allTexts.map(t => ({ text: t, bold: false, italic: false, size: null, color: null })) }];
                }

                shapes.push(shape);
              }

              // Parse picture elements (including from flattened groups)
              const picMatches = flatXml.match(/<p:pic[\s>][\s\S]*?<\/p:pic>/g) || [];
              for (const picXml of picMatches) {
                const off = picXml.match(/<a:off x="(\d+)" y="(\d+)"/);
                const ext2 = picXml.match(/<a:ext cx="(\d+)" cy="(\d+)"/);
                if (!off || !ext2) continue;
                const embedId = picXml.match(/r:embed="([^"]+)"/)?.[1];
                const mediaPath = embedId ? rels[embedId] : null;
                const blobUrl = mediaPath ? imageMap[mediaPath] : null;
                shapes.push({
                  type: 'image', src: blobUrl || null,
                  x: emuPctX(off[1]), y: emuPctY(off[2]),
                  w: emuPctX(ext2[1]), h: emuPctY(ext2[2]),
                  fill: blobUrl ? null : '#E5E7EB',
                  paragraphs: blobUrl ? [] : [{ align: 'center', runs: [{ text: '[Image]', bold: false, italic: false, size: 8, color: '#9CA3AF' }] }],
                });
              }

              // Sort by area: largest shapes (backgrounds) first → lowest z-index
              // Smaller shapes (text, icons) render on top
              shapes.sort((a, b) => (b.w * b.h) - (a.w * a.h));

              // Get title text for thumbnail
              const allTexts = shapes.flatMap(s => s.paragraphs?.flatMap(p => p.runs.map(r => r.text)) || []);
              slides.push({ shapes, number: i, bg, title: allTexts[0] || `Slide ${i}`, summary: allTexts.slice(1, 4).join(' ') });
              i++;
            }
            // Load Google Fonts for any font families found in slides
            const fontFamilies = new Set();
            for (const slide of slides) {
              for (const shape of slide.shapes) {
                for (const p of (shape.paragraphs || [])) {
                  for (const r of p.runs) {
                    if (r.fontFamily && !['Calibri', 'Arial', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia', 'Tahoma', 'Trebuchet MS', 'Impact', 'Comic Sans MS'].includes(r.fontFamily)) {
                      fontFamilies.add(r.fontFamily);
                    }
                  }
                }
              }
            }
            if (fontFamilies.size > 0) {
              const families = [...fontFamilies].map(f => f.replace(/ /g, '+')).join('&family=');
              const linkId = 'pptx-google-fonts';
              if (!document.getElementById(linkId)) {
                const link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
                document.head.appendChild(link);
              } else {
                document.getElementById(linkId).href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
              }
            }

            setPptxSlides(slides.length ? slides : [{ shapes: [], number: 1, bg: '#fff', title: 'Empty', summary: '' }]);
            setActiveSlide(0);
          } catch (err) {
            console.error('PPTX parse error:', err);
            setPptxSlides([{ shapes: [], number: 1, bg: '#fff', title: 'Error: ' + err.message, summary: '' }]);
          }
        })
        .catch(err => {
          console.error('PPTX fetch error:', err);
          setPptxSlides([{ shapes: [], number: 1, bg: '#fff', title: 'Could not load file', summary: err.message }]);
        });
    }
  }, [file?.id, downloadUrl, rawUrl]);

  if (!file) return null;

  const mime = file.mime_type || '';
  const ext = file.name?.split('.').pop()?.toLowerCase() || '';
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  const isAudio = mime.startsWith('audio/');
  const isPdf = mime.includes('pdf');
  const isText = mime.includes('text/') || mime.includes('json') || mime.includes('xml') || mime.includes('javascript') || ['md', 'txt', 'csv', 'log', 'env', 'yml', 'yaml'].includes(ext);
  const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);

  const handleRename = async () => {
    if (!newName.trim() || newName === file.name) { setRenaming(false); return; }
    await fetch(`${API}/files/${file.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim() })
    });
    file.name = newName.trim();
    setRenaming(false);
    onFileChanged?.();
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${file.name}"?`)) return;
    await fetch(`${API}/files/${file.id}`, { method: 'DELETE', credentials: 'include' });
    onFileChanged?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <FileIcon mimeType={mime} name={file.name} />
            <div className="min-w-0">
              {renaming ? (
                <input
                  type="text" value={newName} onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
                  onBlur={handleRename} autoFocus
                  className="text-sm font-medium text-gray-900 border border-blue-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-500 w-64"
                />
              ) : (
                <h3 className="font-medium text-gray-900 text-sm truncate">{file.name}</h3>
              )}
              <p className="text-xs text-gray-500">{formatSize(file.size_bytes)} &middot; {formatDate(file.updated_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={async () => {
                const res = await fetch(`${API}/files/${file.id}/shares`, { credentials: 'include' });
                const data = await res.json();
                setShareData(data);
                setShowShare(true);
              }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Share">
              <Users className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={() => setRenaming(true)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Rename">
              <Edit2 className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={handleDelete} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
            <button onClick={handleDownloadFile}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Download">
              <Download className="w-4 h-4 text-gray-500" />
            </button>
            <button onClick={async (e) => {
                e.stopPropagation();
                const btn = e.target.closest('button');
                btn.disabled = true;
                btn.innerHTML = '<span class="inline-flex items-center gap-1.5"><span class="animate-spin inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full"></span> Converting...</span>';
                try {
                  const res = await fetch(`/api/work/documents/from-file/${file.id}`, {
                    method: 'POST', credentials: 'include',
                  });
                  const d = await res.json();
                  if (d.document) {
                    const dt = d.document.doc_type;
                    const route = dt === 'spreadsheet' ? 'sheet' : dt === 'presentation' ? 'slides' : 'doc';
                    window.location.href = `/work/${route}/${d.document.id}`;
                  } else {
                    console.error('Edit failed:', d.error);
                    e.target.closest('button').disabled = false;
                    e.target.closest('button').innerHTML = '<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg> Edit';
                  }
                } catch (err) {
                  console.error('Edit error:', err.message);
                  e.target.closest('button').disabled = false;
                  e.target.closest('button').innerHTML = '<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg> Edit';
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60">
              <Edit2 className="w-3.5 h-3.5" />
              Edit
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Preview — unified renderer: server converts office docs to PDF,
            native types stream directly. Same output as email + iOS. */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <FileRenderer src={`${API}/files/${file.id}/rendered`} filename={file.name} />
        </div>

        {/* Tags */}
        {file.tags?.length > 0 && (
          <div className="px-5 py-2 border-t border-gray-100 flex items-center gap-2">
            <Tag className="w-3.5 h-3.5 text-gray-400" />
            {file.tags.map(t => (
              <span key={t.id} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: t.color + '20', color: t.color }}>
                {t.name}
              </span>
            ))}
          </div>
        )}

        {/* Share Dialog */}
        {showShare && shareData && (
          <div className="border-t border-gray-200 px-5 py-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Share "{file.name}"</h4>

            {/* General access */}
            <div className="flex items-center gap-3 mb-3">
              {shareData.visibility === 'private' ? <Lock className="w-4 h-4 text-gray-400" /> : <Globe className="w-4 h-4 text-green-500" />}
              <select
                value={shareData.visibility || 'private'}
                onChange={async (e) => {
                  const res = await fetch(`${API}/files/${file.id}/sharing`, {
                    method: 'PUT', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ visibility: e.target.value })
                  });
                  const d = await res.json();
                  setShareData(prev => ({ ...prev, ...d }));
                }}
                className="text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="private">Restricted — only people added</option>
                <option value="unlisted">Anyone with the link</option>
                <option value="public">Public — discoverable</option>
              </select>
            </div>

            {/* Share link */}
            {shareData.visibility !== 'private' && shareData.shareToken && (
              <div className="flex items-center gap-2 mb-3 bg-gray-50 rounded-lg px-3 py-2">
                <Link2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  type="text" readOnly
                  value={`${window.location.origin}/work/shared/${shareData.shareToken}`}
                  className="flex-1 text-xs bg-transparent outline-none text-gray-600"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/work/shared/${shareData.shareToken}`); }}
                  className="text-xs text-blue-600 font-medium hover:text-blue-700"
                >
                  Copy
                </button>
              </div>
            )}

            {/* Add people */}
            <div className="flex gap-2 mb-3">
              <input
                type="email" value={shareEmail} onChange={e => setShareEmail(e.target.value)}
                placeholder="Add people by email"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && shareEmail.trim()) {
                    await fetch(`${API}/files/${file.id}/share`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: shareEmail.trim(), permission: 'viewer' })
                    });
                    setShareEmail('');
                    const res = await fetch(`${API}/files/${file.id}/shares`, { credentials: 'include' });
                    setShareData(await res.json());
                  }
                }}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Shared with list */}
            {shareData.shares?.length > 0 && (
              <div className="space-y-1">
                {shareData.shares.map(s => (
                  <div key={s.id} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700">{s.shared_with_email}</span>
                    <div className="flex items-center gap-2">
                      <select
                        defaultValue={s.permission}
                        onChange={async (e) => {
                          await fetch(`${API}/files/${file.id}/share`, {
                            method: 'POST', credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: s.shared_with_email, permission: e.target.value })
                          });
                        }}
                        className="text-xs border-none bg-transparent text-gray-500"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="commenter">Commenter</option>
                        <option value="editor">Editor</option>
                      </select>
                      <button
                        onClick={async () => {
                          await fetch(`${API}/files/${file.id}/share/${s.id}`, { method: 'DELETE', credentials: 'include' });
                          setShareData(prev => ({ ...prev, shares: prev.shares.filter(x => x.id !== s.id) }));
                        }}
                        className="text-xs text-red-400 hover:text-red-600"
                      >Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button onClick={() => setShowShare(false)} className="mt-2 text-xs text-gray-400 hover:text-gray-600">
              Close sharing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Search Modal with split results
function SearchModal({ isOpen, onClose, onSelectFile, selectedProject }) {
  const [query, setQuery] = useState('');
  const [nameResults, setNameResults] = useState([]);
  const [contentResults, setContentResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) { setQuery(''); setNameResults([]); setContentResults([]); return; }
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (query.length < 2) { setNameResults([]); setContentResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q: query });
        if (selectedProject) params.set('projectId', selectedProject.id);
        const res = await fetch(`${API}/search?${params}`, { credentials: 'include' });
        const data = await res.json();
        setNameResults(data.nameResults || []);
        setContentResults(data.contentResults || []);
      } catch {}
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, selectedProject]);

  if (!isOpen) return null;

  const hasResults = nameResults.length > 0 || contentResults.length > 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 flex flex-col max-h-[60vh]" onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder={selectedProject ? `Search in ${selectedProject.name}...` : 'Search all files...'}
            className="flex-1 text-sm outline-none placeholder-gray-400"
            autoFocus
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-4 text-center text-sm text-gray-400">Searching...</div>
          )}

          {!loading && query.length >= 2 && !hasResults && (
            <div className="p-8 text-center text-gray-400">
              <Search className="w-8 h-8 mx-auto mb-2 stroke-1" />
              <p className="text-sm">No results for "{query}"</p>
            </div>
          )}

          {/* Name matches */}
          {nameResults.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">File name matches</span>
                <span className="text-[10px] text-gray-400 ml-2">({nameResults.length})</span>
              </div>
              {nameResults.map(file => (
                <button
                  key={file.id}
                  onClick={() => { onSelectFile(file); onClose(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <FileIcon mimeType={file.mime_type} name={file.name} className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{file.name}</p>
                    <p className="text-[10px] text-gray-400">{file.project_name} &middot; {formatSize(file.size_bytes)}</p>
                  </div>
                  <ExtBadge name={file.name} />
                </button>
              ))}
            </div>
          )}

          {/* Content matches */}
          {contentResults.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Content matches</span>
                <span className="text-[10px] text-gray-400 ml-2">({contentResults.length})</span>
              </div>
              {contentResults.map(file => (
                <button
                  key={file.id}
                  onClick={() => { onSelectFile(file); onClose(); }}
                  className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <FileIcon mimeType={file.mime_type} name={file.name} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{file.name}</p>
                    {file.snippet && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2" dangerouslySetInnerHTML={{
                        __html: file.snippet.replace(/<mark>/g, '<span class="bg-yellow-200 px-0.5 rounded">').replace(/<\/mark>/g, '</span>')
                      }} />
                    )}
                    <p className="text-[10px] text-gray-400 mt-0.5">{file.project_name} &middot; {formatSize(file.size_bytes)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {query.length < 2 && (
            <div className="p-6 text-center text-gray-400 text-sm">
              Type at least 2 characters to search
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [projectMeetings, setProjectMeetings] = useState([]);
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);
  const [meetingRecordings, setMeetingRecordings] = useState({});
  const [expandedTranscriptId, setExpandedTranscriptId] = useState(null);
  const [tags, setTags] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('jubly_work_view') || 'grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const newMenuRef = useRef(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [captureUrl, setCaptureUrl] = useState('');
  const [captureLoading, setCaptureLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm }
  const [toast, setToast] = useState(null); // { message, type: 'error'|'success' }

  // Keyboard shortcut: / to open search
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Load projects + handle deep link
  useEffect(() => {
    fetch(`${API}/projects`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const projectList = d.projects || [];
        setProjects(projectList);
        setLoading(false);

        // Deep link: ?project=ID&folder=ID&file=ID
        const projectId = searchParams.get('project');
        const folderId = searchParams.get('folder');
        const fileId = searchParams.get('file');

        // Try URL param first, then localStorage
        const resolvedProjectId = projectId || localStorage.getItem('jubly_last_project');
        if (resolvedProjectId) {
          const p = projectList.find(pr => pr.id === resolvedProjectId);
          if (p) {
            setSelectedProject(p);
            if (!projectId) setSearchParams({ project: p.id });
            if (folderId) setCurrentFolderId(folderId);
            if (fileId) {
              // Load file details for preview
              fetch(`${API}/files/${fileId}`, { credentials: 'include' })
                .then(r => r.json())
                .then(fd => { if (fd.file) setSelectedFile(fd.file); })
                .catch(() => {});
            }
          }
        }
      })
      .catch(() => setLoading(false));
    fetch(`${API}/tags`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setTags(d.tags || []))
      .catch(() => {});
  }, []);

  // Load folders + files when project/folder changes
  useEffect(() => {
    if (!selectedProject) { setFolders([]); setFiles([]); return; }
    const params = new URLSearchParams();
    params.set('projectId', selectedProject.id);
    if (currentFolderId) params.set('folderId', currentFolderId);
    if (searchQuery) params.set('search', searchQuery);
    if (selectedTag) params.set('tag', selectedTag);

    fetch(`${API}/projects/${selectedProject.id}/folders?${currentFolderId ? `parent_id=${currentFolderId}` : ''}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setFolders(d.folders || []))
      .catch(() => setFolders([]));

    fetch(`${API}/files?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setFiles(d.files || []))
      .catch(() => setFiles([]));

    // Also load documents in this project/folder
    const docParams = new URLSearchParams();
    docParams.set('projectId', selectedProject.id);
    if (currentFolderId) docParams.set('folderId', currentFolderId);
    fetch(`/api/work/documents?${docParams}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setDocuments(d.documents || []))
      .catch(() => setDocuments([]));

    // Load project meetings (via partner API to Imagia)
    if (!currentFolderId) {
      partnerApi('GET', `/meetings?project_id=${selectedProject.id}&limit=20`)
        .then(d => setProjectMeetings(d?.meetings || []))
        .catch(() => setProjectMeetings([]));
    } else {
      setProjectMeetings([]);
    }
  }, [selectedProject, currentFolderId, searchQuery, selectedTag]);

  const navigateToFolder = (folder) => {
    setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentFolderId(folder.id);
    if (selectedProject) setSearchParams({ project: selectedProject.id, folder: folder.id });
  };

  const navigateUp = (index) => {
    if (index < 0) {
      setBreadcrumb([]);
      setCurrentFolderId(null);
      if (selectedProject) setSearchParams({ project: selectedProject.id });
    } else {
      setBreadcrumb(prev => prev.slice(0, index + 1));
      setCurrentFolderId(breadcrumb[index].id);
      if (selectedProject) setSearchParams({ project: selectedProject.id, folder: breadcrumb[index].id });
    }
  };

  const createProject = async () => {
    if (!newProjectName.trim()) return;
    const res = await fetch(`${API}/projects`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newProjectName.trim() })
    });
    const d = await res.json();
    if (d.project) {
      setProjects(prev => [d.project, ...prev]);
      setSelectedProject(d.project);
    }
    setNewProjectName('');
    setShowNewProject(false);
  };

  const createFolder = async () => {
    if (!newFolderName.trim() || !selectedProject) return;
    const res = await fetch(`${API}/folders`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId: selectedProject.id, parentId: currentFolderId, name: newFolderName.trim() })
    });
    const d = await res.json();
    if (d.folder) setFolders(prev => [...prev, d.folder]);
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const uploadFiles = async (fileList) => {
    if (!fileList?.length || !selectedProject) return;
    setUploading(true);
    for (const file of fileList) {
      const form = new FormData();
      form.append('file', file);
      form.append('projectId', selectedProject.id);
      if (currentFolderId) form.append('folderId', currentFolderId);
      try {
        const res = await fetch(`${API}/files/upload`, { method: 'POST', credentials: 'include', body: form });
        const d = await res.json();
        if (d.file) setFiles(prev => [d.file, ...prev]);
      } catch {}
    }
    setUploading(false);
  };

  const handleUpload = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedProject) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (selectedProject && e.dataTransfer.files?.length) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const deleteFile = async (file) => {
    setConfirmDialog({
      title: 'Delete file',
      message: `Are you sure you want to delete "${file.name}"?`,
      onConfirm: async () => {
        await fetch(`${API}/files/${file.id}`, { method: 'DELETE', credentials: 'include' });
        setFiles(prev => prev.filter(f => f.id !== file.id));
        setConfirmDialog(null);
      },
    });
    return;
  };

  const renameFile = async (file, newName) => {
    if (!newName?.trim() || newName === file.name) return;
    await fetch(`${API}/files/${file.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim() })
    });
    setFiles(prev => prev.map(f => f.id === file.id ? { ...f, name: newName.trim() } : f));
  };

  const [contextMenu, setContextMenu] = useState(null); // { file, x, y }
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const handleContextMenu = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ file, x: e.clientX, y: e.clientY });
  };

  const toggleStar = async (file) => {
    await fetch(`${API}/files/${file.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_starred: !file.is_starred })
    });
    setFiles(prev => prev.map(f => f.id === file.id ? { ...f, is_starred: !f.is_starred } : f));
  };

  return (
    <div className="flex bg-white" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Mobile sidebar backdrop */}
      {mobileSidebar && (
        <div className="fixed inset-0 z-30 bg-black/40 sm:hidden" onClick={() => setMobileSidebar(false)} />
      )}

      {/* Sidebar — Projects */}
      <div className={`w-60 border-r border-gray-200 flex flex-col flex-shrink-0 bg-white
        fixed sm:relative inset-y-0 left-0 z-40 sm:z-auto pt-14 sm:pt-0 transition-transform sm:translate-x-0
        ${mobileSidebar ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={() => setShowNewProject(true)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> New Project
          </button>
        </div>

        {showNewProject && (
          <div className="p-3 border-b border-gray-200">
            <input
              type="text" value={newProjectName} onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createProject()}
              placeholder="Project name" autoFocus
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <div className="flex gap-1 mt-1">
              <button onClick={createProject} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Create</button>
              <button onClick={() => setShowNewProject(false)} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Projects</div>
          {projects.map(p => (
            <button
              key={p.id}
              onClick={() => { setSelectedProject(p); setBreadcrumb([]); setCurrentFolderId(null); setSelectedTag(null); setSearchParams({ project: p.id }); setMobileSidebar(false); localStorage.setItem('jubly_last_project', p.id); }}
              className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors ${
                selectedProject?.id === p.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color }} />
              <span className="truncate flex-1">{p.name}</span>
              <span className="text-[10px] text-gray-400">{p.fileCount}</span>
            </button>
          ))}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="border-t border-gray-200 p-2">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Tags</div>
            {tags.map(t => (
              <button
                key={t.id}
                onClick={() => { const next = selectedTag === t.id ? null : t.id; setSelectedTag(next); if (next) { setCurrentFolderId(null); setBreadcrumb([]); } }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-colors ${
                  selectedTag === t.id ? 'bg-gray-100 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Hash className="w-3 h-3" style={{ color: t.color }} />
                {t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <button onClick={() => setMobileSidebar(true)} className="sm:hidden p-1.5 -ml-1 rounded-lg hover:bg-gray-100 text-gray-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
            {/* Breadcrumb */}
            {selectedProject ? (
              <div className="flex items-center gap-1 text-sm min-w-0">
                <button onClick={() => navigateUp(-1)} className="text-gray-500 hover:text-gray-900 truncate font-medium">
                  {selectedProject.name}
                </button>
                {breadcrumb.map((b, i) => (
                  <span key={b.id} className="flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 text-gray-400" />
                    <button onClick={() => navigateUp(i)} className="text-gray-500 hover:text-gray-900 truncate">
                      {b.name}
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-gray-500">Select a project to get started</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <button
              onClick={() => setShowSearch(true)}
              className="flex items-center bg-gray-100 rounded-lg px-3 py-1.5 hover:bg-gray-200 transition-colors"
            >
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-400 ml-1.5">Search files...</span>
              <kbd className="ml-3 text-[10px] text-gray-400 bg-gray-200 px-1 rounded hidden sm:inline">/</kbd>
            </button>

            {/* View toggle */}
            <div className="flex bg-gray-100 rounded p-0.5">
              <button onClick={() => { setViewMode('grid'); localStorage.setItem('jubly_work_view', 'grid'); }} className={`p-1 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}>
                <Grid3X3 className="w-3.5 h-3.5 text-gray-600" />
              </button>
              <button onClick={() => { setViewMode('list'); localStorage.setItem('jubly_work_view', 'list'); }} className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}>
                <List className="w-3.5 h-3.5 text-gray-600" />
              </button>
            </div>

            {/* New button + dropdown */}
            {selectedProject && (
              <div className="relative" ref={newMenuRef}>
                <button onClick={() => setShowNewMenu(!showNewMenu)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                  New
                </button>
                {showNewMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowNewMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-40">
                      <button onClick={async () => {
                        setShowNewMenu(false);
                        const res = await fetch('/api/work/documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: selectedProject.id, folderId: currentFolderId, docType: 'document' }) });
                        const d = await res.json();
                        if (d.document) window.location.href = `/work/doc/${d.document.id}`;
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Document</div>
                          <div className="text-[11px] text-gray-500">Rich text with collaboration</div>
                        </div>
                      </button>
                      <button onClick={async () => {
                        setShowNewMenu(false);
                        const res = await fetch('/api/work/documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: selectedProject.id, folderId: currentFolderId, docType: 'spreadsheet', title: 'Untitled Spreadsheet' }) });
                        const d = await res.json();
                        if (d.document) window.location.href = `/work/sheet/${d.document.id}`;
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18M10 3v18M14 3v18" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Spreadsheet</div>
                          <div className="text-[11px] text-gray-500">Formulas, cells, data</div>
                        </div>
                      </button>
                      <button onClick={async () => {
                        setShowNewMenu(false);
                        const res = await fetch('/api/work/documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: selectedProject.id, folderId: currentFolderId, docType: 'presentation', title: 'Untitled Presentation' }) });
                        const d = await res.json();
                        if (d.document) window.location.href = `/work/slides/${d.document.id}`;
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Presentation</div>
                          <div className="text-[11px] text-gray-500">Slides with speaker notes</div>
                        </div>
                      </button>
                      <button onClick={async () => {
                        setShowNewMenu(false);
                        const res = await fetch('/api/work/documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: selectedProject.id, folderId: currentFolderId, docType: 'brainstorm', title: 'Untitled Brainstorm' }) });
                        const d = await res.json();
                        if (d.document) window.location.href = `/work/brainstorm/${d.document.id}`;
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Brainstorm</div>
                          <div className="text-[11px] text-gray-500">Canvas with framework zones</div>
                        </div>
                      </button>
                      <button onClick={async () => {
                        setShowNewMenu(false);
                        const res = await fetch('/api/work/documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId: selectedProject.id, folderId: currentFolderId, docType: 'collective', title: 'Untitled Collective Doc' }) });
                        const d = await res.json();
                        if (d.document) window.location.href = `/work/collective/${d.document.id}`;
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Collective Doc</div>
                          <div className="text-[11px] text-gray-500">Controlled editing with voting</div>
                        </div>
                      </button>
                      <button onClick={() => {
                        setShowNewMenu(false);
                        setCaptureUrl('');
                        setShowUrlModal(true);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Collective Page</div>
                          <div className="text-[11px] text-gray-500">Annotate a web page</div>
                        </div>
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button onClick={() => { setShowNewMenu(false); setShowNewFolder(true); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <FolderPlus className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Folder</div>
                          <div className="text-[11px] text-gray-500">Organize your files</div>
                        </div>
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <label className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Upload className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Upload files</div>
                          <div className="text-[11px] text-gray-500">From your computer</div>
                        </div>
                        <input type="file" multiple onChange={(e) => { setShowNewMenu(false); handleUpload(e); }} className="hidden" />
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* New folder inline */}
        {showNewFolder && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-blue-500" />
            <input
              type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createFolder()}
              placeholder="Folder name" autoFocus
              className="text-sm px-2 py-1 border border-blue-200 rounded focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <button onClick={createFolder} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Create</button>
            <button onClick={() => setShowNewFolder(false)} className="text-xs text-gray-500">Cancel</button>
          </div>
        )}

        {/* Content area — drop zone */}
        <div
          className={`flex-1 overflow-y-auto p-4 relative transition-colors ${isDragOver ? 'bg-blue-50' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragOver && (
            <div className="absolute inset-4 border-2 border-dashed border-blue-400 rounded-2xl bg-blue-50/80 flex items-center justify-center z-30 pointer-events-none">
              <div className="text-center">
                <Upload className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                <p className="text-blue-600 font-medium">Drop files to upload</p>
                <p className="text-blue-400 text-sm">{currentFolderId ? 'to this folder' : 'to ' + (selectedProject?.name || 'project')}</p>
              </div>
            </div>
          )}
          {!selectedProject ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <FolderOpen className="w-16 h-16 mb-4 stroke-1" />
              <p className="text-lg font-medium text-gray-500">Welcome to Jubly Work</p>
              <p className="text-sm mt-1">Create a project to start organizing your files</p>
            </div>
          ) : (
            <>
              {/* Active tag filter chip */}
              {selectedTag && (
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Tagged</span>
                  <button onClick={() => setSelectedTag(null)}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100">
                    #{tags.find((t) => t.id === selectedTag)?.name || 'tag'}
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}

              {/* Folders (hidden while filtering by a tag or search — those show a flat, project-wide file list) */}
              {folders.length > 0 && !selectedTag && !searchQuery && (
                <div className="mb-4">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Folders</div>
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2' : 'space-y-1'}>
                    {folders.map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => navigateToFolder(folder)}
                        className={viewMode === 'grid'
                          ? 'flex flex-col items-center p-4 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all'
                          : 'flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 w-full text-left'
                        }
                      >
                        <FolderOpen className={viewMode === 'grid' ? 'w-10 h-10 text-blue-400 mb-2' : 'w-5 h-5 text-blue-400'} />
                        <span className="text-sm text-gray-800 truncate">{folder.name}</span>
                        {viewMode === 'list' && (
                          <span className="text-xs text-gray-400 ml-auto">{folder.fileCount} files</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {files.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Files</div>
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {files.map(file => (
                        <button
                          key={file.id}
                          onClick={() => { if (renamingFileId === file.id) return; setSelectedFile(file); const p = {}; if (selectedProject) p.project = selectedProject.id; if (currentFolderId) p.folder = currentFolderId; p.file = file.id; setSearchParams(p); }}
                          onContextMenu={(e) => handleContextMenu(e, file)}
                          className="flex flex-col items-center p-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all group relative"
                        >
                          {/* Action dots */}
                          <button onClick={(e) => handleContextMenu(e, file)}
                            className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity z-10">
                            <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <div className="relative mb-2">
                            <FileIcon mimeType={file.mime_type} name={file.name} className="w-10 h-10" />
                            {file.is_starred && <Star className="w-3 h-3 text-amber-400 fill-amber-400 absolute -top-1 -right-1" />}
                          </div>
                          <div className="w-full text-center">
                            {renamingFileId === file.id ? (
                              <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { renameFile(file, renameValue); setRenamingFileId(null); } if (e.key === 'Escape') setRenamingFileId(null); }}
                                onBlur={() => { renameFile(file, renameValue); setRenamingFileId(null); }}
                                onClick={e => e.stopPropagation()} autoFocus
                                className="text-xs text-center w-full border border-blue-300 rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-blue-500" />
                            ) : (
                              <p className="text-xs text-gray-800 leading-tight break-words line-clamp-2">{file.name}</p>
                            )}
                            <div className="flex items-center justify-center gap-1.5 mt-1">
                              <ExtBadge name={file.name} />
                              <span className="text-[10px] text-gray-400">{formatSize(file.size_bytes)}</span>
                            </div>
                          </div>
                          {file.tags?.length > 0 && (
                            <div className="flex gap-0.5 mt-1.5 flex-wrap justify-center">
                              {file.tags.slice(0, 3).map(t => (
                                <span key={t.id} className="text-[9px] px-1.5 py-px rounded-full font-medium" style={{ backgroundColor: t.color + '20', color: t.color }}>
                                  {t.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {files.map(file => (
                        <div
                          key={file.id}
                          onClick={() => { if (renamingFileId === file.id) return; setSelectedFile(file); const p = {}; if (selectedProject) p.project = selectedProject.id; if (currentFolderId) p.folder = currentFolderId; p.file = file.id; setSearchParams(p); }}
                          onContextMenu={(e) => handleContextMenu(e, file)}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 w-full text-left group cursor-pointer"
                        >
                          <FileIcon mimeType={file.mime_type} name={file.name} className="w-5 h-5 flex-shrink-0" />
                          {renamingFileId === file.id ? (
                            <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { renameFile(file, renameValue); setRenamingFileId(null); } if (e.key === 'Escape') setRenamingFileId(null); }}
                              onBlur={() => { renameFile(file, renameValue); setRenamingFileId(null); }}
                              onClick={e => e.stopPropagation()} autoFocus
                              className="text-sm flex-1 border border-blue-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-500" />
                          ) : (
                            <span className="text-sm text-gray-800 truncate flex-1">{file.name}</span>
                          )}
                          {file.tags?.map(t => (
                            <span key={t.id} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: t.color + '20', color: t.color }}>
                              {t.name}
                            </span>
                          ))}
                          <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(file.size_bytes)}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0 w-20">{formatDate(file.updated_at)}</span>
                          <button onClick={e => { e.stopPropagation(); toggleStar(file); }}
                            className="p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Star className={`w-3.5 h-3.5 ${file.is_starred ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                          </button>
                          <button onClick={(e) => handleContextMenu(e, file)}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity">
                            <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Documents */}
              {documents.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Documents</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {documents.map(doc => {
                      const docUrl = `/work/${doc.doc_type === 'spreadsheet' ? 'sheet' : doc.doc_type === 'presentation' ? 'slides' : doc.doc_type === 'brainstorm' ? 'brainstorm' : doc.doc_type === 'collective' ? 'collective' : doc.doc_type === 'collective_page' ? 'page' : 'doc'}/${doc.id}`;
                      const docIcon = doc.doc_type === 'spreadsheet' ? { bg: 'bg-green-50', color: 'text-green-600', path: 'M3 10h18M3 14h18M10 3v18M14 3v18' }
                        : doc.doc_type === 'presentation' ? { bg: 'bg-orange-50', color: 'text-orange-600', path: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' }
                        : doc.doc_type === 'brainstorm' ? { bg: 'bg-purple-50', color: 'text-purple-600', path: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' }
                        : doc.doc_type === 'collective' ? { bg: 'bg-indigo-50', color: 'text-indigo-600', path: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
                        : doc.doc_type === 'collective_page' ? { bg: 'bg-teal-50', color: 'text-teal-600', path: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9' }
                        : { bg: 'bg-blue-50', color: 'text-blue-600', path: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' };
                      return (
                        <div key={doc.id}
                          onClick={() => { window.location.href = docUrl; }}
                          className="flex flex-col items-center p-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all group relative cursor-pointer">
                          {/* Three dots menu */}
                          <button onClick={(e) => {
                              e.stopPropagation();
                              setContextMenu({ x: e.clientX, y: e.clientY, doc });
                            }}
                            className="absolute top-1 right-1 p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity z-10">
                            <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <div className="mb-2">
                            <div className={`w-10 h-10 rounded-lg ${docIcon.bg} flex items-center justify-center`}>
                              <svg className={`w-5 h-5 ${docIcon.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={docIcon.path} /></svg>
                            </div>
                          </div>
                          <p className="text-xs text-gray-800 leading-tight break-words line-clamp-2 text-center">{doc.title}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{
                            doc.doc_type === 'brainstorm' ? 'Brainstorm' :
                            doc.doc_type === 'spreadsheet' ? 'Spreadsheet' :
                            doc.doc_type === 'presentation' ? 'Presentation' :
                            doc.doc_type === 'collective' ? 'Collaborate' :
                            doc.doc_type === 'collective_page' ? 'Page Review' :
                            doc.word_count ? `${doc.word_count} words` :
                            formatDate(doc.updated_at)
                          }</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Meetings */}
              {projectMeetings.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Meetings</div>
                  <div className="space-y-2">
                    {projectMeetings.map(m => {
                      const dur = m.actual_start && m.actual_end ? Math.floor((new Date(m.actual_end) - new Date(m.actual_start)) / 1000) : null;
                      const isExpanded = expandedMeetingId === m.id;
                      const recs = meetingRecordings[m.id] || [];
                      return (
                        <div key={m.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                          <button
                            onClick={() => {
                              if (isExpanded) { setExpandedMeetingId(null); return; }
                              setExpandedMeetingId(m.id);
                              if (!meetingRecordings[m.id]) {
                                partnerApi('GET', `/meetings/${m.id}/recordings`)
                                  .then(d => setMeetingRecordings(prev => ({ ...prev, [m.id]: d?.recordings || [] })))
                                  .catch(() => {});
                              }
                            }}
                            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                                <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">{m.title}</p>
                                <p className="text-[10px] text-gray-400">
                                  {new Date(m.actual_start || m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  {dur ? ` · ${dur < 3600 ? `${Math.floor(dur/60)}m` : `${Math.floor(dur/3600)}h ${Math.floor((dur%3600)/60)}m`}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {m.recording_count > 0 && (
                                <span className="flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-medium text-red-600">
                                  <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" /></svg>
                                  {m.recording_count}
                                </span>
                              )}
                              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${m.status === 'ended' ? 'bg-gray-100 text-gray-500' : m.status === 'live' ? 'bg-green-100 text-green-600' : m.status === 'scheduled' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-500'}`}>
                                {m.status}
                              </span>
                              <svg className={`h-3.5 w-3.5 text-gray-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-2">
                              {m.summary && (
                                <div className="rounded-lg bg-blue-50 p-2.5">
                                  <p className="text-[9px] font-semibold uppercase tracking-wider text-blue-500 mb-0.5">AI Summary</p>
                                  <p className="text-[11px] text-gray-700 whitespace-pre-wrap">{m.summary}</p>
                                </div>
                              )}
                              {recs.length === 0 ? (
                                <p className="text-[11px] text-gray-400 text-center py-1">No recordings</p>
                              ) : recs.map(r => (
                                <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-2.5">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-gray-500">
                                      {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                      {r.duration_seconds ? ` · ${r.duration_seconds < 3600 ? `${Math.floor(r.duration_seconds/60)}m` : `${Math.floor(r.duration_seconds/3600)}h`}` : ''}
                                    </span>
                                    <div className="flex gap-1">
                                      {(r.storage_key || r.download_url) && (
                                        <a href={`${IMAGIA_API}/api/partner/meetings/${m.id}/recordings/${r.id}/download`} target="_blank" rel="noopener noreferrer"
                                          className="rounded bg-gray-100 p-1 text-gray-500 hover:bg-gray-200" title="Download">
                                          <Download className="w-3 h-3" />
                                        </a>
                                      )}
                                      {r.transcription && (
                                        <button onClick={() => setExpandedTranscriptId(expandedTranscriptId === r.id ? null : r.id)}
                                          className={`rounded p-1 ${expandedTranscriptId === r.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} title="Transcript">
                                          <FileText className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {expandedTranscriptId === r.id && r.transcription && (
                                    <div className="mt-2 rounded bg-gray-50 p-2 text-[11px] text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">{r.transcription}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {folders.length === 0 && files.length === 0 && documents.length === 0 && projectMeetings.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Upload className="w-12 h-12 mb-3 stroke-1" />
                  <p className="text-sm font-medium text-gray-500">This {currentFolderId ? 'folder' : 'project'} is empty</p>
                  <p className="text-xs mt-1">Drag files here or use the upload button</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Context Menu — works for both files and documents */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
          <div className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-44"
            style={{ left: contextMenu.x, top: contextMenu.y }}>
            {contextMenu.file ? (
              <>
                <button onClick={() => { setRenamingFileId(contextMenu.file.id); setRenameValue(contextMenu.file.name); setContextMenu(null); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Edit2 className="w-3.5 h-3.5" /> Rename
                </button>
                <button onClick={() => { toggleStar(contextMenu.file); setContextMenu(null); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Star className={`w-3.5 h-3.5 ${contextMenu.file.is_starred ? 'fill-amber-400 text-amber-400' : ''}`} />
                  {contextMenu.file.is_starred ? 'Unstar' : 'Star'}
                </button>
                <button onClick={async () => {
                    const f = contextMenu.file;
                    setContextMenu(null);
                    try {
                      const res = await fetch(`${API}/files/${f.id}/raw`, { credentials: 'include' });
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = f.name;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch { window.open(`${API}/files/${f.id}/download`, '_blank'); }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={async () => {
                    const res = await fetch(`${API}/files/${contextMenu.file.id}/reindex`, { method: 'POST', credentials: 'include' });
                    const d = await res.json();
                    if (d.tags) setFiles(prev => prev.map(f => f.id === contextMenu.file.id ? { ...f, tags: d.tags } : f));
                    setContextMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Tag className="w-3.5 h-3.5" /> Detect tags
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { deleteFile(contextMenu.file); setContextMenu(null); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            ) : contextMenu.doc ? (
              <>
                <button onClick={async () => {
                    const newTitle = prompt('Rename document:', contextMenu.doc.title);
                    if (newTitle && newTitle !== contextMenu.doc.title) {
                      await fetch(`/api/work/documents/${contextMenu.doc.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: newTitle }) });
                      setDocuments(prev => prev.map(d => d.id === contextMenu.doc.id ? { ...d, title: newTitle } : d));
                    }
                    setContextMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Edit2 className="w-3.5 h-3.5" /> Rename
                </button>
                <button onClick={async () => {
                    const starred = !contextMenu.doc.is_starred;
                    await fetch(`/api/work/documents/${contextMenu.doc.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_starred: starred }) });
                    setDocuments(prev => prev.map(d => d.id === contextMenu.doc.id ? { ...d, is_starred: starred } : d));
                    setContextMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Star className={`w-3.5 h-3.5 ${contextMenu.doc.is_starred ? 'fill-amber-400 text-amber-400' : ''}`} />
                  {contextMenu.doc.is_starred ? 'Unstar' : 'Star'}
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={async () => {
                    const docToDelete = contextMenu.doc;
                    setContextMenu(null);
                    setConfirmDialog({
                      title: 'Delete document',
                      message: `Are you sure you want to delete "${docToDelete.title}"?`,
                      onConfirm: async () => {
                        await fetch(`/api/work/documents/${docToDelete.id}`, { method: 'DELETE', credentials: 'include' });
                        setDocuments(prev => prev.filter(d => d.id !== docToDelete.id));
                        setConfirmDialog(null);
                      },
                    });
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            ) : null}
          </div>
        </>
      )}

      {/* Search Modal */}
      <SearchModal
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        selectedProject={selectedProject}
        onSelectFile={(file) => {
          setSelectedFile(file);
          const p = {};
          if (file.project_id) p.project = file.project_id;
          if (file.folder_id) p.folder = file.folder_id;
          p.file = file.id;
          setSearchParams(p);
        }}
      />

      {/* File Preview Modal */}
      <FilePreview
        file={selectedFile}
        onClose={() => { setSelectedFile(null); const p = {}; if (selectedProject) p.project = selectedProject.id; if (currentFolderId) p.folder = currentFolderId; setSearchParams(p); }}
        onFileChanged={() => {
          // Refresh files list
          if (selectedProject) {
            const params = new URLSearchParams({ projectId: selectedProject.id });
            if (currentFolderId) params.set('folderId', currentFolderId);
            fetch(`${API}/files?${params}`, { credentials: 'include' })
              .then(r => r.json()).then(d => setFiles(d.files || [])).catch(() => {});
          }
        }}
      />

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmDialog(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-gray-600 mb-6">{confirmDialog.message}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={confirmDialog.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {/* URL Capture Modal */}
      {showUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowUrlModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Capture Web Page</h3>
            <p className="text-xs text-gray-500 mb-4">Enter the URL of the page you want to annotate</p>
            <input
              type="url"
              value={captureUrl}
              onChange={e => setCaptureUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && captureUrl.trim()) {
                  e.preventDefault();
                  setCaptureLoading(true);
                  fetch('/api/work/documents/from-url', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: captureUrl.trim(), projectId: selectedProject?.id, folderId: currentFolderId }) })
                    .then(r => r.json())
                    .then(d => { if (d.document) window.location.href = `/work/page/${d.document.id}`; })
                    .catch(() => setToast({ type: 'error', message: 'Failed to capture page' }))
                    .finally(() => { setCaptureLoading(false); setShowUrlModal(false); });
                }
              }}
              placeholder="https://example.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowUrlModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                disabled={!captureUrl.trim() || captureLoading}
                onClick={() => {
                  setCaptureLoading(true);
                  fetch('/api/work/documents/from-url', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: captureUrl.trim(), projectId: selectedProject?.id, folderId: currentFolderId }) })
                    .then(r => r.json())
                    .then(d => { if (d.document) window.location.href = `/work/page/${d.document.id}`; })
                    .catch(() => setToast({ type: 'error', message: 'Failed to capture page' }))
                    .finally(() => { setCaptureLoading(false); setShowUrlModal(false); });
                }}
                className="px-4 py-2 text-sm bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {captureLoading ? 'Capturing...' : 'Capture'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-20 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-3 opacity-70 hover:opacity-100">&times;</button>
          {setTimeout(() => setToast(null), 5000) && null}
        </div>
      )}
    </div>
  );
}
